import React from 'react'
import { Box, Typography } from '@mui/material'
import { GX_INK_500, GX_INK_700 } from '../growthos-shared/GrowthOsUi'
import {
  Badge, DataTable, HubspotLink, MBR_TITLE, StatusPill, compactCurrency, dealLink, integer,
} from './pfUi'

/** The one deal-level table, reused by every drill so the rows always look and sort the same. */
export function DealTable({ rows, hub, showDays = false, showTraceReason = false }) {
  const columns = [
    {
      key: 'deal', header: 'Deal', cellSx: { whiteSpace: 'normal', minWidth: 200 },
      cell: (d) => {
        const href = dealLink(hub, d.dealId)
        return (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {href
              ? <Box component="a" href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                  sx={{ fontWeight: 500, color: '#4338ca', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>{d.dealName}</Box>
              : <Box component="span" sx={{ fontWeight: 500 }}>{d.dealName}</Box>}
            {d.movedPipeline && <Badge title="This deal has moved between pipelines — its stage history spans both, and the funnel counts the full journey.">moved</Badge>}
          </Box>
        )
      },
      sortValue: (d) => d.dealName,
    },
    { key: 'pipeline', header: 'Pipeline', cell: (d) => d.pipelineLabel, sortValue: (d) => d.pipelineLabel },
    { key: 'status', header: 'Status', title: 'Open / Won / Lost, derived from the deal’s current stage group.', cell: (d) => <StatusPill status={d.status} />, sortValue: (d) => d.status },
    { key: 'stage', header: 'Current Stage', title: 'The exact HubSpot stage, not the canonical rung — so pipeline-specific stages stay visible.', cell: (d) => d.currentStageLabel, sortValue: (d) => d.currentStageLabel },
    ...(showDays ? [{
      key: 'days', header: 'Days in Stage', align: 'right',
      title: 'Days since the deal entered its current stage, from that stage’s entry timestamp.',
      cell: (d) => (d.daysInCurrentStage == null ? '—' : (
        <Box component="span" sx={{ color: d.daysInCurrentStage >= 90 ? '#e11d48' : undefined, fontWeight: d.daysInCurrentStage >= 90 ? 600 : undefined }}>
          {integer(d.daysInCurrentStage)}
        </Box>
      )),
      sortValue: (d) => d.daysInCurrentStage,
    }] : []),
    { key: 'source', header: 'Source', title: 'Deal Source Sub Category — the canonical channel taxonomy.', cell: (d) => d.subcategory, sortValue: (d) => d.subcategory },
    ...(showTraceReason ? [{
      key: 'fix', header: 'What to fix in HubSpot', cellSx: { whiteSpace: 'normal', minWidth: 230 },
      title: 'Why this deal cannot be traced back to a marketing lead, phrased as the action needed. Three causes, three different fixes: attach a contact to the deal, log that contact as a lead, or correct the lead’s Lead Source.',
      cell: (d) => (d.traceGapReason
        ? <Badge sx={{ bgcolor: 'rgba(180,83,9,0.12)', color: '#b45309', borderColor: 'transparent', whiteSpace: 'normal' }}>{d.traceGapReason}</Badge>
        : '—'),
      sortValue: (d) => d.traceGapReason || '',
    }] : []),
    { key: 'owner', header: 'Sales POC', cell: (d) => d.owner, sortValue: (d) => d.owner },
    { key: 'mbr', header: 'Est. MBR', align: 'right', title: MBR_TITLE, cell: (d) => compactCurrency(d.estimatedMbr), sortValue: (d) => d.estimatedMbr },
    { key: 'created', header: 'Created', cell: (d) => d.createdDate || '—', sortValue: (d) => d.createdDate || '' },
    { key: 'hubspot', header: 'HubSpot', title: 'Opens this deal in HubSpot, where the fix is applied.', cell: (d) => <HubspotLink hub={hub} dealId={d.dealId} />, sortValue: (d) => d.dealId },
  ]
  return (
    <DataTable
      rows={rows}
      dense
      initialPageSize={25}
      rowKey={(d) => d.dealId}
      filter={(d, q) => `${d.dealName} ${d.dealId} ${d.owner} ${d.subcategory} ${d.pipelineLabel} ${d.currentStageLabel}`.toLowerCase().includes(q)}
      filterPlaceholder="Filter deals…"
      emptyText="No deals."
      columns={columns}
    />
  )
}

/** The actionable list behind a Gap cell: deals that can't be traced back to a marketing lead. */
export function TraceGapPanel({ label, rows, hub }) {
  if (!rows.length) {
    return (
      <Typography sx={{ p: 2, fontSize: 13, color: GX_INK_500 }}>
        Every deal in <strong>{label}</strong> traces back to a marketing lead — nothing to fix here.
      </Typography>
    )
  }
  return (
    <Box sx={{ p: 1.5 }}>
      <Typography sx={{ mb: 1, fontSize: 13, color: GX_INK_700 }}>
        <strong>{integer(rows.length)}</strong> deal{rows.length === 1 ? '' : 's'} in <strong>{label}</strong> can’t be traced back to a marketing lead.
        {' '}Open each in HubSpot and apply the fix in the last column — once corrected, Deals and Lead-traceable converge.
      </Typography>
      <DealTable rows={rows} hub={hub} showTraceReason />
    </Box>
  )
}
